/**
 * Die Kostenbetrachtung — Formen des Servers und die Rechnung der Fläche.
 *
 * ## Warum das nicht in `KostenPanel.svelte` steht
 *
 * Dieselbe Begründung wie bei `stationsart.ts`: Die Tests dieses Projekts
 * laufen ohne Browser (`tests/run.mjs`). Was in einer Vorlage steht, ist damit
 * unerreichbar; was hier steht, ist eine gewöhnliche Funktion. Und es sind
 * genau die Stellen, an denen ein stiller Fehler teuer wäre — eine Differenz
 * mit falschem Vorzeichen, eine Nutzlast ohne Währung, eine Vignettenliste,
 * die beim Speichern die Routing-Vorgaben mitnimmt.
 *
 * ## Warum nicht in `planner/`
 *
 * Der Rechenkern läuft auf dem iPhone in JavaScriptCore und wird über
 * `npm run kern` versioniert. Hier steht nichts, was der Kern braucht: Die
 * Zahlen rechnet der **Server** (`kosten.py`), diese Datei nimmt sie
 * entgegen, ordnet sie für eine Tabelle und baut die Nutzlast eines
 * Formulars. Ein Eingriff in `planner/` hätte die Kernversion gehoben, ohne
 * dass sich am Kern etwas ändert.
 *
 * ## Die drei Regeln des Servers, die hier durchgehalten werden
 *
 * 1. **`plan.maut` ist eine Schätzung oder `null`, nie ein Preis.** Bis zur
 *    Kostenautomatik war der Wert ausnahmslos `null`; seit der Server die
 *    Vignetten aus `settings` und die Mautkilometer der Etappen (`toll_m`)
 *    rechnet, steht dort meist eine Zahl — und daneben in `plan.offen`, was
 *    darin **nicht** steckt (AT-Sondermaut, Etappen ohne Mautangabe). Beides
 *    gehört an die Zeile: die Zahl als Schätzung gekennzeichnet, die Lücke mit
 *    Begründung. Als glatte `0 €` gezeigt behauptete der Posten, Maut koste
 *    nichts.
 * 2. **`ist.*` zählt nur Belege und Diktate.** `geschaetzt_summe` steht
 *    daneben und ist in **keiner** Aufteilung enthalten. Wer sie zur Summe
 *    addiert, bekommt eine Zahl, die sich aus der Tabelle darunter nicht mehr
 *    ergibt.
 * 3. **`abweichung` ist `null` ohne Plan.** Ohne Planwert ist jede Abweichung
 *    hundert Prozent, und das ist keine Auskunft.
 *
 * **Reine Funktionen** — kein DOM, kein Netz, keine Uhr.
 */
import { euro, euroJe } from '../gestaltung/geld';
import { istAbgeschlossen, type MitAbschluss } from './reiseabschluss';
// Der Antrieb entscheidet, welche der beiden Energiezeilen etwas zu sagen hat.
// Der Typ steht in `reiseart.ts`, weil er zur Reise gehört und nicht zu den
// Kosten — hier wird er nur gelesen.
import type { Antrieb } from './reiseart';

/* ══ Was der Server sagt ═════════════════════════════════════════════════
 *
 * Die Formen von `GET /trips/{id}/kosten`, eins zu eins. Sie stehen hier und
 * nicht in `api.ts`, weil die Funktionen darunter mit ihnen rechnen und beides
 * zusammen prüfbar sein soll; `api.ts` holt sie sich von hier, wie es das bei
 * `Tagebucheintrag` schon tut.
 */

/**
 * Die neun Kategorien in der Reihenfolge des ENUM (`kosten.KATEGORIEN`).
 *
 * Als Daten und nicht als Vereinigungstyp allein: Die Auswahl im Formular,
 * die Zeilenfolge der Tabelle und die Prüfung der Nutzlast brauchen dieselbe
 * Liste. Drei Aufzählungen davon liefen früher oder später auseinander — und
 * die Abweichung fiele erst am 422 des Servers auf.
 */
export const KATEGORIEN = [
  'laden',
  'maut',
  'uebernachtung',
  'essen',
  'eintritt',
  'tanken',
  'faehre',
  'parken',
  'sonstiges'
] as const;

export type Kostenart = (typeof KATEGORIEN)[number];

/** Das ausgeschriebene Wort — Auswahl im Formular, Kopf der Tabellenzeile. */
export const KATEGORIE_TEXT: Record<Kostenart, string> = {
  laden: 'Laden',
  maut: 'Maut',
  uebernachtung: 'Übernachtung',
  essen: 'Essen',
  eintritt: 'Eintritt',
  tanken: 'Tanken',
  faehre: 'Fähre',
  parken: 'Parken',
  sonstiges: 'Sonstiges'
};

/**
 * Das Kürzel — Tagesverlauf und Kostenmarke im Tagebuch.
 *
 * Nur dort kürzer, wo das volle Wort eine Zeile sprengt: Aus `Übernachtung`
 * wird `Nacht`, aus `Sonstiges` wird `Sonst.`. Der Rest bleibt gleich; ein
 * zweites Wort für dieselbe Sache wäre eine Vokabel mehr, die niemand lernt.
 */
export const KATEGORIE_KURZ: Record<Kostenart, string> = {
  laden: 'Laden',
  maut: 'Maut',
  uebernachtung: 'Nacht',
  essen: 'Essen',
  eintritt: 'Eintritt',
  tanken: 'Tanken',
  faehre: 'Fähre',
  parken: 'Parken',
  sonstiges: 'Sonst.'
};

/**
 * Woher ein Betrag kommt.
 *
 * `geschaetzt` ist der Sonderfall, um den es geht: Er zählt nicht in die
 * Summe, sondern daneben. Heute erzeugt der Server noch keine Schätzungen
 * (das kommt mit der Lade-Schätzung), die Anzeige trägt sie aber schon —
 * sonst tauchte die erste Schätzung in einer Fläche auf, die sie nicht kennt.
 */
export type Kostenherkunft = 'beleg' | 'diktat' | 'geschaetzt';

/** Ein Posten, den der Server nicht beziffern kann — mit Grund. */
export interface OffenerPosten {
  posten: string;
  grund: string;
}

/**
 * Die Planseite von `GET /trips/{id}/kosten`.
 *
 * ## Die Form ist ein flaches Wörterbuch, und das ist der Haken
 *
 * Der Server legt jeden Posten als eigenes Feld neben `summe`, `offen` und
 * `geschaetzt` (siehe `kosten.plan_kosten`) — keine Liste, keine Kennungen.
 * Wer die Tabelle daraus baut, muss die Feldnamen kennen, und genau dort ist
 * am 26.8.2026 die Summe auseinandergefallen: `ausfluege` kam dazu, stand
 * sofort in `summe` und in keiner Zeile, weil hier drei Namen abgetippt
 * standen. 178 Euro, die man nur durch Nachzählen fand.
 *
 * Deshalb werden die Posten seither **nicht mehr abgetippt**, sondern aus der
 * Antwort gelesen (`planposten()`): Alles, was nicht `summe`, `offen` oder
 * `geschaetzt` heißt, ist ein Posten — auch der, den der Server morgen
 * dazulegt. Die Felder hier stehen nur noch für die Typprüfung und für den
 * Satz, der an ihnen hängt.
 */
export interface Planseite {
  /** `null`, solange für die Reise kein Ladeplan gespeichert wurde. */
  laden: number | null;
  /**
   * Der Sprit einer Verbrenner-Reise — Etappen-km × l/100 km × Richtwert.
   *
   * Es ist ausdrücklich eine **Hochrechnung** und nie ein Preis: Der Server
   * rechnet mit runden Länder-Richtwerten (`kraftstoff.RICHTWERTE_EUR_L`) und
   * mit dem Verbrauch, der am Fahrzeug steht. Deshalb trägt die Zeile
   * dieselbe Tilde wie die Maut (siehe `IMMER_GESCHAETZT`).
   *
   * `null` oder fehlend heißt: nicht gerechnet — bei einer Elektro-Reise,
   * weil es dort nichts zu tanken gibt, bei einer Verbrenner-Reise, weil der
   * Verbrauch am Fahrzeug fehlt. Der zweite Fall ist eine Lücke mit Abhilfe
   * und steht deshalb als offener Posten da (`SPRIT_OHNE_VERBRAUCH`).
   *
   * Optional, weil ein älterer Server den Posten nicht kennt.
   */
  sprit?: number | null;
  /**
   * Vignetten plus geschätzte Streckenmaut — oder `null`.
   *
   * `null` heißt: Der Server konnte gar nichts rechnen (keine Vignette
   * hinterlegt, keine Etappe mit Mautangabe). Eine Zahl heißt **Schätzung**,
   * nie Preis: Die Vignettenbeträge sind Anhaltswerte, die Streckenmaut ist
   * aus Mautkilometern hochgerechnet. Was der Server nicht beziffern kann,
   * steht zusätzlich in `offen` — eine Zahl hier schließt eine Lücke dort
   * also nicht aus.
   */
  maut: number | null;
  /** Preis je Nacht mal Nächte, über alle nicht ausgelassenen Stationen. */
  uebernachtung: number;
  /**
   * Ausflüge und die Punkte eingehängter Tagespläne (seit dem 26.8.2026).
   *
   * `cost` einer Ausflugsstation zählt hier **einmal** und nicht mal Nächte —
   * ein Ausflug hat keine (siehe `kosten._plan_ausfluege`). Dazu die
   * `kosten_eur` der Punkte jedes eingehängten Tagesplans.
   *
   * Optional, weil ein älterer Server den Posten nicht kennt. Eine `0` ist
   * hier eine Aussage und keine Lücke: Wo kein Ausflug ist, ist nichts zu
   * bezahlen.
   */
  ausfluege?: number;
  summe: number;
  offen: OffenerPosten[];
  /**
   * Posten, die dastehen, aber mit Anhaltswerten gerechnet sind.
   *
   * Dieselbe Form wie `offen` und eine andere Bedeutung — `offen` heißt
   * „konnte nicht gerechnet werden", `geschaetzt` heißt „gerechnet, aber mit
   * Anhaltswerten". Beides in eine Liste zu werfen hieße, eine fehlende Zahl
   * und eine ungenaue gleich zu behandeln.
   *
   * Optional, weil ein älterer Server das Feld nicht schickt; dann trägt die
   * Maut ihre Kennzeichnung weiterhin aus der Regel (siehe `tabellenzeilen`).
   */
  geschaetzt?: OffenerPosten[];
}

export interface Kostentag {
  datum: string;
  summe: number;
  je_kategorie: Record<string, number>;
}

export interface Istseite {
  je_kategorie: Record<string, number>;
  je_tag: Kostentag[];
  /** ISO-Kürzel oder `unbekannt` — die Lücke steht in der Tabelle. */
  je_land: Record<string, number>;
  summe: number;
  /** Steht **neben** der Summe und in keiner Aufteilung. */
  geschaetzt_summe: number;
  /**
   * Was in welcher Währung bezahlt wurde — Original und Euro nebeneinander.
   *
   * Optional, weil ein älterer Server das Feld nicht schickt; dann steht die
   * Zeile unter der Summe einfach nicht da. Euro-Posten kommen mit
   * `original: null` — in Euro gibt es keinen Originalbetrag, der sich vom
   * Euro-Betrag unterscheidet, und eine wiederholte Zahl wäre eine Angabe
   * ohne Inhalt.
   */
  je_waehrung?: Waehrungszeile[] | null;
}

/** Eine Zeile aus `ist.je_waehrung` — so, wie der Server sie schickt. */
export interface Waehrungszeile {
  code: string;
  /** Der bezahlte Betrag in dieser Währung. `null` bei Euro. */
  original: number | null;
  eur: number;
}

export interface Kennzahlen {
  eur_pro_km: number | null;
  strom_eur_100km: number | null;
  /**
   * Das Pendant für Verbrenner — was 100 km Sprit gekostet haben.
   *
   * Genau **eine** der beiden steht je Reise da: Eine Elektro-Reise hat keine
   * Spritkosten, eine Verbrenner-Reise keine Stromkosten, und beide
   * Beschriftungen nebeneinander mit einem Gedankenstrich in der einen wären
   * eine Kennzahl, die es für diese Reise nicht gibt (siehe
   * `energiekennzahl`).
   *
   * Optional, weil ein älterer Server das Feld nicht schickt.
   */
  sprit_eur_100km?: number | null;
  eur_pro_tag: number | null;
}

/** Die vollständige Antwort von `GET /trips/{id}/kosten`. */
export interface Kostenstand {
  plan: Planseite;
  ist: Istseite;
  kennzahlen: Kennzahlen;
  abweichung: { eur: number; prozent: number } | null;
}

/* ══ Die Tabelle je Kategorie ════════════════════════════════════════════ */

/**
 * Eine Zeile der Tabelle Plan | Ist | Differenz.
 *
 * `plan === null` heißt zweierlei, und der Unterschied ist der Punkt:
 *
 * - `offen: true` — der Server **kann** nicht beziffern und sagt warum
 *   (Maut immer, Laden ohne gespeicherten Ladeplan). Die Zelle zeigt „offen"
 *   samt Grund.
 * - `offen: false` — für diese Kategorie gibt es überhaupt keinen Plan
 *   (eine Reise plant kein Abendessen). Die Zelle bleibt leer.
 *
 * Beides als `0 €` zu zeigen wäre die bequeme Lösung und die falsche: Eine
 * Null in einer Kostenspalte behauptet, es koste nichts.
 */
export interface Tabellenzeile {
  /**
   * Der Name der Zeile — eine Kostenart **oder** ein Planposten.
   *
   * `string` und nicht `Kostenart`: Die Plan- und die Ist-Seite des Servers
   * führen nicht dieselben Namen. `ausfluege` gibt es nur im Plan, `essen`
   * nur im Ist, und was morgen dazukommt, kennt dieser Typ heute nicht. Eine
   * Zeile, die es nicht in die Aufzählung schafft, verschwände samt ihrem
   * Betrag aus der Tabelle — und nicht aus der Summe.
   */
  kategorie: string;
  wort: string;
  plan: number | null;
  ist: number;
  /** `null`, wo es keinen Plan gibt — eine Differenz gegen nichts ist keine. */
  differenz: number | null;
  offen: boolean;
  grund: string | null;
  /**
   * Ist der Planwert hochgerechnet statt beziffert?
   *
   * Heute genau bei der Maut: Der Server addiert Vignetten-Anhaltswerte und
   * eine Hochrechnung aus Mautkilometern (`toll_m × €/km`). Beides kann um ein
   * Drittel danebenliegen, und die Zeile daneben (Übernachtung) ist ein
   * abgesprochener Preis. In derselben Schrift nebeneinander liest man beide
   * als dasselbe.
   *
   * Zwei Quellen, und die Reihenfolge ist Absicht: die Liste `plan.geschaetzt`
   * des Servers, wenn er eine schickt — und sonst die Regel „die Maut ist es
   * immer". Die Regel bleibt, weil ein älterer Server das Feld nicht hat und
   * die Maut dort trotzdem eine Hochrechnung ist.
   */
  geschaetzt: boolean;
  /**
   * Der Vorbehalt des Servers zu dieser Zahl — oder `null`.
   *
   * Steht am Tildensymbol und sagt, **womit** gerechnet wurde. Getrennt von
   * `grund`, weil die beiden Verschiedenes sagen: `grund` heißt „konnte nicht
   * gerechnet werden", das hier heißt „gerechnet, aber mit Anhaltswerten".
   */
  anhaltswert: string | null;
}

/**
 * Eine Zeile der Fußzeile — was die Tabelle darüber ergibt.
 *
 * **Gerechnet und nicht geholt.** Das ist der ganze Punkt dieser Datei seit
 * dem 27.8.2026: Die Summe zeigt, was die Zeilen darüber ergeben, und nicht,
 * was der Server als `summe` nennt. Beides ist bei einer heilen Antwort
 * dieselbe Zahl; gehen sie auseinander, sieht man es (siehe `Summenabgleich`),
 * statt es von Hand nachzuzählen.
 */
export interface Tabellenfuss {
  plan: number;
  ist: number;
  /** `null` ohne Plansumme — eine Differenz gegen nichts ist keine. */
  differenz: number | null;
  prozent: number | null;
}

/**
 * Die Serversumme passt nicht zu den Posten, die er mitgeschickt hat.
 *
 * Der Fall, für den diese ganze Umstellung da ist. Er tritt auf, wenn der
 * Server einen Posten in `summe` zählt, den diese Fläche nicht als Zeile
 * bekommt — etwa weil er ihn in einer Form schickt, die sich nicht addieren
 * lässt. Dann steht der Unterschied als Hinweis unter der Tabelle: Eine
 * Summe, die mehr behauptet als ihre Posten, merkt sonst niemand.
 */
export interface Summenabgleich {
  seite: 'plan' | 'ist';
  /** `Plan` oder `Ist` — für den Satz im Hinweis. */
  wort: string;
  server: number;
  zeilen: number;
}

/** Zeilen, Fußzeile und der Abgleich — alles, was die Tabelle braucht. */
export interface Kostentabelle {
  zeilen: Tabellenzeile[];
  fuss: Tabellenfuss;
  /** Leer, solange Serversumme und Zeilen übereinstimmen. Der Normalfall. */
  abgleich: Summenabgleich[];
}

/** Zwei Nachkommastellen, wie überall bei Geld. */
function cent(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Die Felder der Planseite, die **kein** Kostenposten sind.
 *
 * Die Aufzählung geht bewusst in diese Richtung. Eine Liste der bekannten
 * Posten war genau der Fehler vom 26.8.2026: Sie veraltet still, sobald der
 * Server einen dazulegt, und der neue Posten steckt dann in der Summe und in
 * keiner Zeile. Eine Liste der Nicht-Posten veraltet auch — aber in die
 * harmlose Richtung: Es stünde eine Zeile zu viel da, und die sieht man.
 */
const PLAN_KEIN_POSTEN = new Set(['summe', 'offen', 'geschaetzt']);

/**
 * Die Wörter für Planposten, die **keine** Kostenart sind.
 *
 * `laden`, `maut` und `uebernachtung` heißen auf beiden Seiten gleich und
 * stehen deshalb in `KATEGORIE_TEXT`; hier steht nur, was es allein im Plan
 * gibt. Was in keiner der beiden Tabellen steht, zeigt seinen Rohnamen —
 * `faehren_reservierung` ist hässlich und sagt mehr als eine verschwundene
 * Zeile.
 */
export const PLANPOSTEN_TEXT: Record<string, string> = {
  ausfluege: 'Ausflüge',
  // „Sprit" und nicht „Tanken": Die Ist-Kategorie heißt `tanken` und ist der
  // Beleg von der Tankstelle; das hier ist die Hochrechnung für die geplante
  // Strecke. Zwei Zeilen mit demselben Wort wären in der Tabelle Plan | Ist
  // genau die Verwechslung, die man nicht auflösen kann.
  sprit: 'Sprit'
};

/**
 * Planposten, deren Zahl **immer** eine Hochrechnung ist.
 *
 * Die Maut, weil sie Vignetten-Anhaltswerte und Mautkilometer addiert; der
 * Sprit, weil er mit runden Länder-Richtwerten je Liter rechnet. Beide können
 * um ein Drittel danebenliegen, und die Zeile daneben (Übernachtung) ist ein
 * abgesprochener Preis — in derselben Schrift liest man beide als dasselbe.
 *
 * Die Regel bleibt neben `plan.geschaetzt` bestehen, weil ein älterer Server
 * die Liste nicht schickt und die Zahl dort trotzdem eine Hochrechnung ist.
 */
const IMMER_GESCHAETZT = new Set(['maut', 'sprit']);

/**
 * Der offene Posten, wenn am Fahrzeug kein Verbrauch steht.
 *
 * Wortlaut aus dem Vertrag („Basislücken, Teil 1" § 1). Er **nennt die
 * Abhilfe** und nicht die Lücke, und das ist der Unterschied zu „Laden steht
 * offen", das bei einer Verbrenner-Reise vorher dastand: Dort war weder etwas
 * zu laden noch etwas zu tun.
 */
export const SPRIT_OHNE_VERBRAUCH = 'Verbrauch am Fahrzeug eintragen';

/**
 * Welche Energieposten zu diesem Antrieb **nicht** gehören.
 *
 * Eine Funktion und keine zwei Bedingungen an zwei Stellen: Die Weiche gilt
 * für die Tabellenzeilen **und** für die heimatlosen Lücken darunter. Ohne
 * den zweiten Ort stünde die gestrichene Zeile prompt als „laden steht offen"
 * unter der Tabelle — dieselbe Falschaussage, einen Absatz tiefer.
 *
 * Gestrichen wird ausschließlich, was **keine Zahl** trägt: Einen gerechneten
 * Betrag wegzulassen hieße, ihn in `plan.summe` und in keiner Zeile zu haben.
 */
function fremdeEnergie(
  planwerte: Map<string, number | null>,
  antrieb?: Antrieb | null
): Set<string> {
  const weg = new Set<string>();
  if (antrieb === 'verbrenner' && planwerte.get('laden') == null) weg.add('laden');
  if (antrieb === 'elektro' && planwerte.get('sprit') == null) weg.add('sprit');
  return weg;
}

/** Das ausgeschriebene Wort eines Postens — notfalls sein Rohname. */
export function postenwort(posten: string): string {
  return KATEGORIE_TEXT[posten as Kostenart] ?? PLANPOSTEN_TEXT[posten] ?? posten;
}

/** Ein Posten der Planseite, wie er aus der Antwort gelesen wird. */
export interface Planposten {
  posten: string;
  /** `null` heißt „lässt sich nicht sagen" — nie 0. */
  eur: number | null;
}

/**
 * Die Posten der Planseite — **aus der Antwort gelesen, nicht abgetippt**.
 *
 * Alles in `plan`, was nicht `summe`, `offen` oder `geschaetzt` heißt. Ein
 * Feld mit `null` ist ein Posten ohne Zahl (die Maut ohne Route, das Laden
 * ohne gespeicherten Ladeplan) und bleibt in der Liste: Seine Zeile sagt
 * „offen".
 *
 * Was weder Zahl noch `null` ist — eine Liste, ein Objekt, was der Server
 * eines Tages an dieser Stelle schickt —, fällt heraus, denn addieren lässt
 * es sich nicht. Still ist das trotzdem nicht: Es steckt dann in `plan.summe`
 * und nicht in den Zeilen, und genau diesen Unterschied meldet
 * `kostentabelle().abgleich`.
 */
export function planposten(stand: Kostenstand | null): Planposten[] {
  const plan = stand?.plan as Record<string, unknown> | null | undefined;
  if (!plan || typeof plan !== 'object') return [];
  const heraus: Planposten[] = [];
  for (const [posten, wert] of Object.entries(plan)) {
    if (PLAN_KEIN_POSTEN.has(posten)) continue;
    if (wert == null) {
      heraus.push({ posten, eur: null });
      continue;
    }
    if (typeof wert !== 'number' || !Number.isFinite(wert)) continue;
    heraus.push({ posten, eur: cent(wert) });
  }
  return heraus;
}

/**
 * Die Tabelle Plan | Ist | Differenz — Zeilen, Fußzeile und Abgleich.
 *
 * ## Eine Quelle, und zwar der Server
 *
 * Beide Spalten kommen aus derselben Antwort: die Plan-Spalte aus den Posten
 * (`planposten()`), die Ist-Spalte aus `ist.je_kategorie`. Der Browser rechnet
 * hier nichts nach — er kennt weder Tagesplan-Preise noch EZB-Kurse. Was er
 * tut, ist addieren, was er zeigt.
 *
 * ## Welche Zeilen es gibt
 *
 * Gezeigt wird eine Zeile, wenn sie **etwas zu sagen hat**: ein Ist-Betrag,
 * ein Planwert oder ein ausdrücklich offener Posten. Alle neun Kategorien
 * immer zu zeigen hieße, sieben leere Zeilen zu tragen, damit zwei gefüllte
 * darin verschwinden.
 *
 * Die Namen kommen aus drei Quellen und in dieser Reihenfolge: die neun
 * Kategorien des ENUM, dann was der Plan zusätzlich mitbringt, dann was das
 * Ist zusätzlich mitbringt. Die feste Reihenfolge vorn ist Absicht — eine
 * Tabelle, deren Zeilen beim Nachtragen einer Ausgabe die Plätze tauschen,
 * liest sich beim zweiten Mal nicht mehr. Und dass hinten überhaupt etwas
 * angehängt wird, ist der eigentliche Fix: `ausfluege` steht im Plan und in
 * keinem ENUM.
 */
export function kostentabelle(
  stand: Kostenstand | null,
  antrieb?: Antrieb | null
): Kostentabelle {
  const leer: Kostentabelle = {
    zeilen: [],
    fuss: { plan: 0, ist: 0, differenz: null, prozent: null },
    abgleich: []
  };
  if (!stand) return leer;

  const ist = stand.ist?.je_kategorie ?? {};
  // Die Begründungen des Servers nach Posten, damit die Zeile sie tragen kann.
  const gruende = new Map<string, string>();
  for (const o of stand.plan?.offen ?? []) gruende.set(o.posten, o.grund);
  const anhaltswerte = new Map<string, string>();
  for (const o of stand.plan?.geschaetzt ?? []) anhaltswerte.set(o.posten, o.grund);

  const planwerte = new Map<string, number | null>();
  for (const p of planposten(stand)) planwerte.set(p.posten, p.eur);

  /*
   * Die Energiezeile, die zu diesem Antrieb gehört — und nur die.
   *
   * Bis zum 13.9.2026 stand an einer Verbrenner-Reise „Laden steht offen":
   * ein offener Posten, zu dem es nichts zu tun gab, weil die Reise nichts
   * lädt. Und der Sprit fehlte ganz. Die Weiche hier räumt beides auf:
   *
   * - Verbrenner: kein `laden`, dafür `sprit`. Fehlt der Verbrauch am
   *   Fahrzeug, steht der Posten offen da und **nennt die Abhilfe**.
   * - Elektro: kein `sprit`.
   *
   * Gestrichen wird ausschließlich, was **keine Zahl** trägt. Einen
   * gerechneten Betrag wegzulassen wäre der schwerere Fehler: Er steckt dann
   * in `plan.summe` und in keiner Zeile — genau die Lücke, die der
   * `abgleich` unten melden muss (siehe `Summenabgleich`).
   *
   * Ohne Angabe (`antrieb` fehlt) bleibt alles, wie der Server es schickt:
   * Wer den Antrieb nicht kennt, soll nicht raten.
   */
  for (const name of fremdeEnergie(planwerte, antrieb)) {
    planwerte.delete(name);
    gruende.delete(name);
  }
  if (antrieb === 'verbrenner' && planwerte.get('sprit') == null && !gruende.has('sprit')) {
    planwerte.set('sprit', null);
    gruende.set('sprit', SPRIT_OHNE_VERBRAUCH);
  }

  const namen: string[] = [...KATEGORIEN];
  for (const name of [...planwerte.keys(), ...Object.keys(ist)]) {
    if (!namen.includes(name)) namen.push(name);
  }

  const zeilen: Tabellenzeile[] = [];
  for (const name of namen) {
    const p = planwerte.get(name) ?? null;
    const i = cent(Number(ist[name] ?? 0));
    const offen = gruende.has(name);
    if (!i && p == null && !offen) continue;
    zeilen.push({
      kategorie: name,
      wort: postenwort(name),
      plan: p,
      ist: i,
      differenz: p == null ? null : cent(i - p),
      offen,
      grund: gruende.get(name) ?? null,
      // Nur wenn überhaupt eine Zahl dasteht: Ohne Planwert gibt es nichts zu
      // kennzeichnen, die Zelle sagt dann „offen".
      geschaetzt: p != null && (IMMER_GESCHAETZT.has(name) || anhaltswerte.has(name)),
      anhaltswert: anhaltswerte.get(name) ?? null
    });
  }

  const planSumme = cent(zeilen.reduce((a, z) => a + (z.plan ?? 0), 0));
  const istSumme = cent(zeilen.reduce((a, z) => a + z.ist, 0));

  const abgleich: Summenabgleich[] = [];
  const serverPlan = cent(Number(stand.plan?.summe ?? 0));
  const serverIst = cent(Number(stand.ist?.summe ?? 0));
  if (serverPlan !== planSumme) {
    abgleich.push({ seite: 'plan', wort: 'Plan', server: serverPlan, zeilen: planSumme });
  }
  if (serverIst !== istSumme) {
    abgleich.push({ seite: 'ist', wort: 'Ist', server: serverIst, zeilen: istSumme });
  }

  return {
    zeilen,
    fuss: {
      plan: planSumme,
      ist: istSumme,
      // Ohne Plan keine Differenz und kein Prozentwert — dieselbe Regel wie im
      // Server (`kosten._abweichung`): Ohne Planwert ist jede Abweichung
      // hundert Prozent, und das ist keine Auskunft.
      differenz: planSumme ? cent(istSumme - planSumme) : null,
      prozent: planSumme ? Math.round(((istSumme - planSumme) / planSumme) * 1000) / 10 : null
    },
    abgleich
  };
}

/**
 * Nur die Zeilen — die häufigste Frage an `kostentabelle()`.
 *
 * Eigene Funktion und keine zweite Rechnung: Sie ruft dieselbe auf. Zwei Wege
 * zu denselben Zeilen wären zwei Gelegenheiten, dass einer davon eine
 * vergisst.
 */
export function tabellenzeilen(
  stand: Kostenstand | null,
  antrieb?: Antrieb | null
): Tabellenzeile[] {
  return kostentabelle(stand, antrieb).zeilen;
}

/**
 * Die offenen Posten, die zu **keiner** gezeigten Zeile gehören.
 *
 * Verglichen wird gegen die Zeilen, die tatsächlich entstehen, und nicht mehr
 * gegen die neun Kategorien: Seit die Tabelle auch Planposten außerhalb des
 * ENUM zeigt (`ausfluege`), stünde deren Begründung sonst zweimal da — einmal
 * an der Zeile und einmal darunter als angeblich heimatlose Lücke.
 *
 * Was übrig bleibt, ist der echte Fall: Der Server meldet eine Lücke unter
 * einem Namen, für den es weder Kategorie noch Planposten gibt. Sie
 * verschwiegen zu lassen ist genau das, was diese Fläche vermeiden soll.
 */
export function offeneOhneZeile(
  stand: Kostenstand | null,
  antrieb?: Antrieb | null
): OffenerPosten[] {
  if (!stand) return [];
  const gezeigt = new Set(tabellenzeilen(stand, antrieb).map((z) => z.kategorie));
  const planwerte = new Map<string, number | null>();
  for (const p of planposten(stand)) planwerte.set(p.posten, p.eur);
  // Was der Antrieb gestrichen hat, ist keine heimatlose Lücke, sondern eine
  // Frage, die es für diese Reise nicht gibt (siehe `fremdeEnergie`).
  const weg = fremdeEnergie(planwerte, antrieb);
  return (stand.plan?.offen ?? []).filter((o) => !gezeigt.has(o.posten) && !weg.has(o.posten));
}

/* ══ Die Energiekennzahl ═════════════════════════════════════════════════ */

/** Eine Kennzahl, wie die Fläche sie setzt — Wert und Beschriftung. */
export interface Energiekennzahl {
  /** Euro je 100 km — die rohe Zahl, formatiert wird in der Fläche. */
  eur_100km: number;
  /** `Strom` oder `Sprit`. */
  wort: string;
}

/**
 * Die eine Energiekennzahl, die zu diesem Antrieb gehört — oder `null`.
 *
 * **Genau eine, nie beide.** Eine Elektro-Reise hat keine Spritkosten und eine
 * Verbrenner-Reise keine Stromkosten; die zweite Zeile wäre immer ein
 * Gedankenstrich und läse sich als fehlende Angabe statt als Frage, die es für
 * diese Reise nicht gibt. Und sie wird **nicht** getauscht: Steht am Antrieb
 * Verbrenner, bleibt die Stromzahl weg, auch wenn eine dasteht — sie käme aus
 * Belegen der Kategorie `laden`, und die gehören dann zu einer anderen Reise
 * oder einem Irrtum beim Eintragen.
 *
 * `null` heißt: keine Grundlage. Ohne Kilometerstände im Tagebuch gibt es
 * keine der beiden, und dann fehlt die Zeile — eine Null behauptete, die
 * Energie sei umsonst.
 */
export function energiekennzahl(
  kennzahlen: Kennzahlen | null | undefined,
  antrieb?: Antrieb | null
): Energiekennzahl | null {
  if (!kennzahlen) return null;
  const roh = antrieb === 'verbrenner' ? kennzahlen.sprit_eur_100km : kennzahlen.strom_eur_100km;
  if (roh == null || !Number.isFinite(Number(roh))) return null;
  return { eur_100km: cent(Number(roh)), wort: antrieb === 'verbrenner' ? 'Sprit' : 'Strom' };
}

/* ══ Der Satz im Kopf ════════════════════════════════════════════════════ */

/**
 * Die eine Zeile, die die Fläche beantwortet.
 *
 * `Panelkopf art="ergebnis"` trägt die Antwort zuerst und die Belege darunter.
 * Der Satz steht hier und nicht in der Vorlage, weil er der Ort ist, an dem
 * eine Kostenübersicht am leichtesten lügt:
 *
 * - **Ohne Ist-Werte** darf nicht „100 % unter Plan" dastehen. Das ist
 *   rechnerisch richtig und Unsinn — es heißt nur, dass noch niemand etwas
 *   eingetragen hat.
 * - **Ohne Plan** darf keine Abweichung stehen. Eine Abweichung von nichts
 *   ist der ganze Betrag, und der steht schon daneben.
 * - **Über und unter Plan** heißen verschiedene Dinge, und das Vorzeichen
 *   allein sagt es niemandem. Deshalb das Wort.
 *
 * `plansumme` ist die Zahl, die die Tabelle darunter zeigt
 * (`kostentabelle().fuss.plan`). Sie wird hereingereicht und nicht hier noch
 * einmal gebildet, damit auf dieser Fläche **eine** Plansumme steht: Der Satz
 * oben und die Zeile „zusammen" dürfen nicht zwei Zahlen nennen. Ohne Angabe
 * gilt die des Servers — für Aufrufer, die keine Tabelle bauen.
 */
export function kopfsatz(stand: Kostenstand | null, plansumme?: number): string {
  if (!stand) return 'Für diese Reise liegt noch keine Kostenrechnung vor.';
  const ist = cent(Number(stand.ist?.summe ?? 0));
  const plan = cent(Number(plansumme ?? stand.plan?.summe ?? 0));
  if (!ist && !plan) return 'Noch keine Ausgabe erfasst und kein Plan gerechnet.';
  if (!ist) return `Noch keine Ausgabe erfasst — geplant sind ${euro(plan, 2)}.`;
  if (!plan) return `${euro(ist, 2)} ausgegeben. Ein Plan steht noch nicht dagegen.`;
  const a = stand.abweichung;
  if (!a) return `${euro(ist, 2)} ausgegeben, geplant waren ${euro(plan, 2)}.`;
  const wort = a.eur >= 0 ? 'über' : 'unter';
  return `${euro(ist, 2)} ausgegeben, geplant waren ${euro(plan, 2)} — ${euro(
    Math.abs(a.eur),
    2
  )} ${wort} Plan.`;
}

/* ══ Der Verlauf je Tag ══════════════════════════════════════════════════ */

export interface Tageszeile {
  datum: string;
  summe: number;
  /** Die Kategorien dieses Tages als Kürzel, teuerste zuerst. */
  kuerzel: string[];
}

/**
 * Der Verlauf, aufsteigend nach Datum.
 *
 * Die Kürzel stehen nach Betrag geordnet und nicht alphabetisch: Wer die Zeile
 * überfliegt, will wissen, wofür der Tag teuer war — und das ist der erste
 * Eintrag, nicht der mit dem frühesten Buchstaben.
 *
 * Sortiert wird hier noch einmal, obwohl der Server es tut: Diese Liste ist
 * eine Zeitachse, und eine Zeitachse, die einmal in der falschen Richtung
 * läuft, fällt niemandem auf — man liest die Summen, nicht die Daten.
 */
export function tageszeilen(stand: Kostenstand | null): Tageszeile[] {
  const tage = stand?.ist?.je_tag ?? [];
  return [...tage]
    .sort((a, b) => (a.datum < b.datum ? -1 : a.datum > b.datum ? 1 : 0))
    .map((t) => ({
      datum: t.datum,
      summe: cent(Number(t.summe ?? 0)),
      kuerzel: Object.entries(t.je_kategorie ?? {})
        .filter(([, v]) => Number(v) > 0)
        .sort((a, b) => Number(b[1]) - Number(a[1]))
        .map(([k]) => KATEGORIE_KURZ[k as Kostenart] ?? k)
    }));
}

/* ══ Je Land ═════════════════════════════════════════════════════════════ */

/**
 * Die Länder, die dieses Reisegebiet berührt.
 *
 * Bewusst keine vollständige Weltliste: Was fehlt, steht als Kürzel da, und
 * ein `PT` versteht man auch ohne Tabelle. Was hier steht, sind die Länder,
 * für die der Server eine Zeitzonenzuordnung führt und die tatsächlich
 * vorkommen.
 */
const LAND_TEXT: Record<string, string> = {
  DE: 'Deutschland',
  AT: 'Österreich',
  CH: 'Schweiz',
  IT: 'Italien',
  SI: 'Slowenien',
  FR: 'Frankreich',
  ES: 'Spanien',
  CZ: 'Tschechien',
  SK: 'Slowakei',
  HU: 'Ungarn',
  PL: 'Polen',
  NL: 'Niederlande',
  BE: 'Belgien',
  LU: 'Luxemburg',
  DK: 'Dänemark',
  HR: 'Kroatien',
  PT: 'Portugal',
  MC: 'Monaco',
  // Nordamerika seit dem 14.9.2026: Der Server ordnet `America/*` jetzt
  // Ländern zu, und ohne diese drei Zeilen stünde über einer Reise von
  // Chicago nach Montreal dreimal ein Kürzel statt eines Landes.
  US: 'USA',
  CA: 'Kanada',
  MX: 'Mexiko'
};

/** Der Schlüssel, unter dem der Server sammelt, was er nicht zuordnen kann. */
export const LAND_UNBEKANNT = 'unbekannt';

export interface Landzeile {
  land: string;
  wort: string;
  eur: number;
}

/**
 * Was in welchem Land ausgegeben wurde — teuerstes Land zuerst.
 *
 * Das ist die Italien-gegen-Österreich-Auskunft, und sie ist der Grund, warum
 * `je_land` überhaupt im Endpunkt steht. Nach Betrag geordnet, weil die Frage
 * „wo war es teuer" lautet und nicht „welche Länder waren es".
 *
 * `unbekannt` bleibt in der Liste und wandert nicht ans Ende: Es ist eine
 * Lücke in den Daten, keine Restkategorie. Wer sie unten versteckt, sieht
 * nicht, dass die Hälfte der Reise nirgends liegt.
 */
export function landzeilen(stand: Kostenstand | null): Landzeile[] {
  const roh = stand?.ist?.je_land ?? {};
  return Object.entries(roh)
    .filter(([, v]) => Number(v) !== 0)
    .map(([land, v]) => ({
      land,
      wort: land === LAND_UNBEKANNT ? 'Land unbekannt' : (LAND_TEXT[land] ?? land),
      eur: cent(Number(v))
    }))
    .sort((a, b) => b.eur - a.eur);
}

/* ══ Währungen ═══════════════════════════════════════════════════════════ */

/**
 * Die Fremdwährungen der Reise — was nicht in Euro bezahlt wurde.
 *
 * Euro fällt heraus, und zwar an zwei Merkmalen: am Code und an einem
 * fehlenden `original`. Beides prüfen und nicht nur eines: Ein Server, der
 * Euro-Posten ohne Code schickt, dürfte hier sonst als Fremdwährung landen,
 * und ein `USD`-Posten ohne Originalbetrag ist eine Zeile, die ihre eigene
 * Zahl nicht kennt — die zu zeigen hieße, 0,00 USD zu behaupten.
 *
 * Geordnet nach Euro-Gegenwert, größter zuerst: Die Frage lautet, wo das Geld
 * geblieben ist, und nicht, welche Währungen vorkamen.
 */
export function fremdwaehrungen(stand: Kostenstand | null): Waehrungszeile[] {
  const roh = stand?.ist?.je_waehrung;
  if (!Array.isArray(roh)) return [];
  return roh
    .filter((z) => {
      const code = (z?.code ?? '').trim().toUpperCase();
      // `z.original != null` **vor** der Zahlprüfung: `Number(null)` ist 0 und
      // damit endlich — ohne diese Zeile käme ein Euro-Posten (`original:
      // null`) mit fremdem Code als „0,00 USD" durch.
      if (z?.original == null) return false;
      return Boolean(code) && code !== 'EUR' && Number.isFinite(Number(z.original));
    })
    .map((z) => ({
      code: (z.code ?? '').trim().toUpperCase(),
      original: cent(Number(z.original)),
      eur: cent(Number(z.eur))
    }))
    .sort((a, b) => b.eur - a.eur);
}

/**
 * Zeitzone → Land, die kleine Fassung — **nur für die Anzeige**.
 *
 * Die Wahrheit steht am Server (`kosten.ZONE_LAND`, vollständig nach
 * `zone1970.tab`); hier liegt eine Handvoll Zonen, damit das Währungsfeld
 * einen Vorschlag machen kann, ohne dafür eine Rundreise zu kosten. Der
 * Browser trifft damit **keine** Entscheidung über eine Ausgabe: In welchem
 * Land eine Ausgabe liegt, rechnet weiterhin der Server.
 *
 * Eine Zone, die hier fehlt, ergibt `null` und damit keinen Vorschlag — eine
 * geratene Zuordnung wäre ein vorbelegtes Feld mit der falschen Währung, und
 * das merkt man erst auf der Abrechnung.
 */
export const ZONE_LAND_ANZEIGE: Record<string, string> = {
  'Europe/Berlin': 'DE',
  'Europe/Busingen': 'DE',
  'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH',
  'Europe/Rome': 'IT',
  'Europe/Ljubljana': 'SI',
  'Europe/Prague': 'CZ',
  'Europe/Bratislava': 'SK',
  'Europe/Budapest': 'HU',
  'Europe/Warsaw': 'PL',
  'Europe/Paris': 'FR',
  'Europe/Monaco': 'MC',
  'Europe/Madrid': 'ES',
  'Europe/Lisbon': 'PT',
  'Europe/Brussels': 'BE',
  'Europe/Amsterdam': 'NL',
  'Europe/Luxembourg': 'LU',
  'Europe/Copenhagen': 'DK',
  'Europe/Zagreb': 'HR',
  // Nordamerika: die Zonen der Reise Chicago → Montreal und ihre Nachbarn.
  // `America/Detroit` ist US und `America/Toronto` ist CA, obwohl beide am
  // selben Fluss liegen — deshalb steht hier jede Zone einzeln und kein
  // Muster auf `America/*`. Montreal fährt unter `America/Toronto`.
  'America/New_York': 'US',
  'America/Detroit': 'US',
  'America/Chicago': 'US',
  'America/Menominee': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Denver': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'America/St_Johns': 'CA',
  'America/Halifax': 'CA',
  'America/Toronto': 'CA',
  'America/Winnipeg': 'CA',
  'America/Regina': 'CA',
  'America/Edmonton': 'CA',
  'America/Vancouver': 'CA'
};

/** Was von einer Station gebraucht wird, um ihr Land zu nennen. */
export interface Landstation {
  land?: string | null;
  tzname?: string | null;
}

/**
 * Das Land einer Station — vom Server, sonst aus ihrer Zeitzone.
 *
 * Die Reihenfolge ist die ganze Regel: Was der Server an der Station
 * mitschickt, gilt; er kennt die vollständige Tabelle und im Zweifel auch die
 * Koordinate. Die Zone ist der Rückfall für einen Server, der das Feld noch
 * nicht liefert. `null` heißt „unbekannt" und nicht „Deutschland".
 */
export function landDerStation(stop: Landstation | null | undefined): string | null {
  const vomServer = (stop?.land ?? '').trim().toUpperCase();
  if (vomServer) return vomServer;
  return ZONE_LAND_ANZEIGE[(stop?.tzname ?? '').trim()] ?? null;
}

/**
 * Land → Währung, der Rückfall, bis der Katalog da ist.
 *
 * **Der Katalog des Servers ist die Wahrheit** (`GET /laender` nennt je Land
 * `waehrung.code`). Diese vier Zeilen sind die Brücke für einen Server, der
 * ihn noch nicht ausliefert, und decken genau die Länder ab, die vorkommen:
 * Nordamerika und die Schweiz. Alles andere bekommt Euro — nicht weil es
 * stimmen müsste, sondern weil die Reisen dieser Anwendung in Europa liegen
 * und ein leeres Feld schon heute Euro heißt. Sobald der Katalog antwortet,
 * zählt er und nicht diese Tabelle.
 */
export const WAEHRUNG_RUECKFALL: Record<string, string> = {
  US: 'USD',
  CA: 'CAD',
  CH: 'CHF'
};

/** Der Währungskatalog, so weit er hier gebraucht wird: Land → Code. */
export type Waehrungskatalog = Record<string, string>;

/**
 * Die Währung eines Landes — aus dem Katalog, sonst aus dem Rückfall.
 *
 * Ohne Land `EUR`: Der Vorschlag ist eine Vorbelegung und keine Aussage, und
 * ein leeres Währungsfeld heißt in dieser Fläche seit je Euro.
 */
export function waehrungFuerLand(
  land: string | null | undefined,
  katalog: Waehrungskatalog | null = null
): string {
  const code = (land ?? '').trim().toUpperCase();
  if (!code) return 'EUR';
  const ausKatalog = (katalog?.[code] ?? '').trim().toUpperCase();
  return ausKatalog || WAEHRUNG_RUECKFALL[code] || 'EUR';
}

/**
 * Die Codes für die Vorschlagsliste am Währungsfeld.
 *
 * Euro steht immer drin und immer vorn: Es ist die Währung, in der gerechnet
 * wird. Der Rest kommt vom Server (`trip.waehrungen`, die Währungen der
 * Länder dieser Reise) — doppelt genannte und leere Einträge fallen weg, die
 * Reihenfolge des Servers bleibt.
 *
 * Ohne Angabe bleibt nur `EUR` übrig. Das ist keine Lücke, sondern der
 * bisherige Zustand: eine Liste mit einem Eintrag statt eines leeren
 * Aufklappers, der aussieht, als wäre etwas kaputt.
 */
export function waehrungsliste(codes: readonly string[] | null | undefined): string[] {
  const raus = ['EUR'];
  for (const roh of codes ?? []) {
    const code = (roh ?? '').trim().toUpperCase();
    if (code && !raus.includes(code)) raus.push(code);
  }
  return raus;
}

/**
 * Was ins Währungsfeld gehört, wenn sich der Vorschlag ändert — oder `null`.
 *
 * `null` heißt **nichts anfassen**, und das ist der wichtigere der beiden
 * Fälle: Die Klebe-Regel dieser Fläche sagt, dass eine einmal eingetippte
 * Währung stehen bleibt (wer in der Schweiz drei Belege nachträgt, tippt
 * sonst dreimal CHF). Ein Vorschlag, der eine getippte Angabe überschreibt,
 * wäre genau der Ärger, den die Regel verhindert.
 *
 * Angefasst wird deshalb nur ein Feld, das leer ist oder noch die zuletzt
 * eingesetzte Vorbelegung trägt. `zuletzt` ist der Code, den diese Funktion
 * beim letzten Mal vorgeschlagen hat — `null` beim ersten Mal.
 *
 * `EUR` ergibt die **leere** Zeichenkette: Leer heißt Euro, und ein
 * vorbelegtes `EUR` wäre eine Entscheidung, die man bei 99 von 100 Ausgaben
 * gar nicht trifft.
 */
export function waehrungsvorgabe(
  vorschlag: string,
  aktuell: string,
  zuletzt: string | null
): string | null {
  const neu = (vorschlag ?? '').trim().toUpperCase();
  if (!neu || neu === zuletzt) return null;
  const vonUns = !zuletzt || zuletzt === 'EUR' ? '' : zuletzt;
  const steht = (aktuell ?? '').trim().toUpperCase();
  if (steht && steht !== vonUns) return null;
  return neu === 'EUR' ? '' : neu;
}

/* ══ Die Kostenmarke am Tagebucheintrag ══════════════════════════════════ */

/** Die Kostenfelder, wie sie an jedem Tagebucheintrag hängen. */
export interface Kostenfelder {
  cost_eur?: number | null;
  cost_kind?: string | null;
  cost_source?: string | null;
  cost_original?: number | null;
  cost_currency?: string | null;
}

export interface Kostenmarke {
  /** `18,40 € · Laden` — Betrag zuerst, weil danach gesucht wird. */
  text: string;
  /** Der Originalbetrag, wenn in fremder Währung bezahlt wurde. */
  herkunft: string | null;
  geschaetzt: boolean;
}

/**
 * Die Marke, die ein Eintrag mit Betrag im Tagebuch trägt — oder `null`.
 *
 * `null` ohne Betrag, und zwar auch dann, wenn eine Kategorie dasteht: Ein
 * `cost_kind` ohne `cost_eur` ist ein Rest und kein Kostenposten (dieselbe
 * Regel wie im Server, siehe `KOSTENSPALTEN`). Eine Marke „Laden“ ohne Zahl
 * verspräche einen Betrag, den es nicht gibt.
 *
 * Der Originalbetrag steht daneben und nicht statt des Euro-Betrags: Gerechnet
 * wird in Euro, gezahlt wurde in Franken, und beide Angaben sind wahr.
 */
export function kostenmarke(e: Kostenfelder | null | undefined): Kostenmarke | null {
  const betrag = e?.cost_eur;
  if (betrag == null || !Number.isFinite(Number(betrag))) return null;
  const art = e?.cost_kind as Kostenart | null | undefined;
  const wort = art && KATEGORIE_KURZ[art] ? KATEGORIE_KURZ[art] : null;
  const original =
    e?.cost_currency && e?.cost_original != null
      ? `${Number(e.cost_original).toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })} ${e.cost_currency}`
      : null;
  return {
    text: wort ? `${euro(Number(betrag), 2)} · ${wort}` : euro(Number(betrag), 2),
    herkunft: original,
    geschaetzt: e?.cost_source === 'geschaetzt'
  };
}

/* ══ Ausgabe eintragen ═══════════════════════════════════════════════════ */

/** Was im Formular steht — alles roh, so wie die Felder es liefern. */
export interface Ausgabeformular {
  /** Deutsch getippt erlaubt: `12,50` wie `12.50`. */
  betrag: string;
  kategorie: Kostenart;
  /** `datetime-local`, also `2026-08-23T14:30`. Leer heißt: jetzt. */
  wann: string;
  notiz: string;
  /** Leer oder `EUR` heißt Euro. Sonst ein ISO-Kürzel wie `CHF`. */
  waehrung: string;
}

export type Nutzlast =
  | { ok: true; daten: Record<string, unknown> }
  | { ok: false; text: string };

/**
 * Aus dem Formular wird die Nutzlast für `POST /trips/{id}/journal`.
 *
 * ## Die Währungsweiche
 *
 * Bei Euro geht `cost_eur` hinaus. Bei fremder Währung gehen
 * `cost_original` **und** `cost_currency` hinaus und `cost_eur` bleibt weg —
 * dann rechnet der Server zum EZB-Tageskurs um. Beides zu schicken hieße, dem
 * Server eine Umrechnung vorzugeben, die er besser kennt; nur den
 * Originalbetrag ohne Code zu schicken beantwortet er zu Recht mit 422
 * („welcher Betrag soll das sein?").
 *
 * ## Was hier absichtlich nicht steht
 *
 * `cost_source` — der Server setzt `diktat`, wenn nichts dabeisteht, und das
 * ist für eine von Hand getippte Ausgabe die richtige Herkunft. `beleg` wäre
 * eine Behauptung über ein Foto, das es nicht gibt.
 *
 * `kind` — die Art des **Eintrags** bleibt `blitzlicht`. Von den neun
 * Kategorien hat nur `laden` ein Gegenstück unter den Eintragsarten; acht
 * bekämen eine erfundene. Die Kategorie steht in `cost_kind`, an genau einer
 * Stelle.
 *
 * `shared: false` — Kosten sind privat per Vorgabe (Grundsatz 4). Der Server
 * lässt `cost_*` zwar ohnehin an der geteilten Seite liegen, aber der Eintrag
 * selbst stünde sonst dort: eine Zeile „Parken" ohne Betrag, die niemandem
 * etwas sagt und trotzdem verrät, dass hier Buch geführt wird.
 */
export function ausgabeNutzlast(f: Ausgabeformular): Nutzlast {
  const roh = (f.betrag ?? '').trim().replace(',', '.');
  if (!roh) return { ok: false, text: 'Ohne Betrag lässt sich nichts eintragen.' };
  const betrag = Number(roh);
  if (!Number.isFinite(betrag)) {
    return { ok: false, text: `${f.betrag} ist keine Zahl. Erlaubt ist etwa 18,40.` };
  }
  if (betrag <= 0) {
    return { ok: false, text: 'Der Betrag muss größer als null sein.' };
  }
  if (!(KATEGORIEN as readonly string[]).includes(f.kategorie)) {
    return { ok: false, text: `${f.kategorie} ist keine bekannte Kategorie.` };
  }

  const code = (f.waehrung ?? '').trim().toUpperCase();
  if (code && code !== 'EUR' && !/^[A-Z]{3}$/.test(code)) {
    return {
      ok: false,
      text: 'Eine Währung ist ein Kürzel aus drei Buchstaben, etwa CHF. Leer heißt Euro.'
    };
  }

  const daten: Record<string, unknown> = {
    title: KATEGORIE_TEXT[f.kategorie],
    note: (f.notiz ?? '').trim() || null,
    cost_kind: f.kategorie,
    shared: false
  };
  if (code && code !== 'EUR') {
    daten.cost_original = cent(betrag);
    daten.cost_currency = code;
  } else {
    daten.cost_eur = cent(betrag);
  }
  const wann = (f.wann ?? '').trim();
  if (wann) daten.ts = wann;
  return { ok: true, daten };
}

/* ══ Vignetten in `trips.settings` ═══════════════════════════════════════ */

/**
 * Der Schlüssel im freien Einstellungsblock.
 *
 * Derselbe Name wie im Server (`_settings_pruefen`), und die Einträge haben
 * dieselbe Form wie der Schlüssel der Maut-Checkliste im Vorbereitungspanel:
 * `AT:Vignette`, also Land und Name. Damit ist die gespeicherte Liste ohne
 * Umrechnung dieselbe, die das Panel abhakt.
 */
const VIGNETTEN = 'vignetten';

/** Der Server nimmt höchstens so viele und schneidet jeden Eintrag ab. */
export const VIGNETTEN_MAX = 20;
export const VIGNETTE_MAX_LAENGE = 40;

/** Der freie Bereich einer Reise, soweit diese Datei ihn braucht. */
type Einstellungen = Record<string, unknown> | null | undefined;

/**
 * Die gespeicherten Vignetten, auf Zeichenketten gesäubert.
 *
 * `settings` kommt vom Server und ist frei — dort kann alles stehen. Was keine
 * brauchbare Kennung ist, fällt heraus, statt als `undefined` in einen
 * Vergleich zu laufen und dort eine Vignette abzuhaken, die niemand
 * angeklickt hat. Dieselbe Vorsicht wie in `planner/quartierstrom.ts`.
 */
export function vignettenAus(settings: Einstellungen): string[] {
  const roh = (settings ?? {})[VIGNETTEN];
  if (!Array.isArray(roh)) return [];
  const raus: string[] = [];
  for (const e of roh) {
    if (typeof e !== 'string') continue;
    const text = e.trim().slice(0, VIGNETTE_MAX_LAENGE);
    if (text && !raus.includes(text)) raus.push(text);
  }
  return raus;
}

/**
 * Die Vignettenliste setzen — und den **ganzen** Einstellungsblock zurückgeben.
 *
 * ## Die Regel, um die es hier geht
 *
 * `PATCH /trips/{id}` ersetzt `settings` als Ganzes (`db.update_trip`). Wer
 * nur `{ vignetten: [...] }` schickt, löscht damit die Straßenvorgaben, die
 * Auto-Nächte und alles andere, was in dem Block steht — lautlos, und man
 * merkt es erst, wenn die Route plötzlich über die Autobahn läuft.
 *
 * Deshalb gibt diese Funktion nicht die Liste zurück, sondern den vollen
 * Block mit ersetzter Liste. Das ist dasselbe Muster wie
 * `quartierstrom.mitLadenAmQuartier`, und es ist der einzige Weg, auf dem die
 * Vignetten geschrieben werden dürfen.
 *
 * Gesäubert wird nach den Regeln des Servers (getrimmt, ohne Leere, ohne
 * Doppelte, gekürzt, gedeckelt): Eine Liste, die der Server mit 422 abweist,
 * soll hier gar nicht erst entstehen — die Fehlermeldung stünde sonst an einer
 * Stelle, an der man nur ein Häkchen gesetzt hat.
 */
export function mitVignetten(settings: Einstellungen, liste: string[]): Record<string, unknown> {
  const sauber: string[] = [];
  for (const e of liste ?? []) {
    if (typeof e !== 'string') continue;
    const text = e.trim().slice(0, VIGNETTE_MAX_LAENGE);
    if (text && !sauber.includes(text)) sauber.push(text);
  }
  return { ...(settings ?? {}), [VIGNETTEN]: sauber.slice(0, VIGNETTEN_MAX) };
}

/* ══ Die Brücke zur Vorbereitung ═════════════════════════════════════════ */

/**
 * Wohin die neun Ist-Kategorien in der Kurzfassung fallen.
 *
 * `planner/ausgaben.ts` kennt fünf Posten, der Server neun Kategorien — und
 * `ausfluege` ist keine davon: Den Posten gibt es nur auf der **Plan**-Seite
 * (ein Ausflug wird geplant; bezahlt wird er als Eintritt, Parken oder
 * Fähre). Auf dieser Seite kommen deshalb weiterhin nur vier Ziele vor.
 *
 * Die Zuordnung steht **hier** und nicht dort: Eine Signaturänderung an
 * `ausgaben.kostenbild()` hieße Kernänderung, Kernbau und neue Kernversion —
 * für eine Übersetzungstabelle, die nur die Weboberfläche braucht.
 */
const POSTEN_JE_KATEGORIE: Record<Kostenart, 'laden' | 'maut' | 'uebernachtung' | 'sonstiges'> = {
  laden: 'laden',
  maut: 'maut',
  uebernachtung: 'uebernachtung',
  essen: 'sonstiges',
  eintritt: 'sonstiges',
  tanken: 'sonstiges',
  faehre: 'sonstiges',
  parken: 'sonstiges',
  sonstiges: 'sonstiges'
};

/**
 * Die Ist-Zeilen für `ausgaben.kostenbild()`.
 *
 * Damit lebt der tote `{#if bild.ist_summe > 0}`-Zweig im Vorbereitungsreiter:
 * Der Hinweis „Ist-Werte kommen aus dem Reisetagebuch" versprach seit jeher
 * etwas, das es nicht gab.
 *
 * Eine Zeile je Kategorie und nicht eine je Eintrag: Die Vorbereitung zeigt
 * eine Kurzfassung, und dafür genügt die Summe. Die Einzelposten stehen im
 * eigenen Reiter, wo sie hingehören — und der holt sie ohnehin direkt aus dem
 * Endpunkt, ohne den Umweg über den Kern.
 *
 * Die Kategorien behalten dabei ihr eigenes Wort, auch wenn sie im selben
 * Posten landen: „Essen 84 €" und „Parken 12 €" sind zwei Zeilen unter
 * `sonstiges` und nicht eine mit 96 €.
 */
export function istZeilenFuerBild(
  stand: Kostenstand | null
): { bezeichnung: string; eur: number; posten: 'laden' | 'maut' | 'uebernachtung' | 'sonstiges' }[] {
  const roh = stand?.ist?.je_kategorie ?? {};
  const zeilen: {
    bezeichnung: string;
    eur: number;
    posten: 'laden' | 'maut' | 'uebernachtung' | 'sonstiges';
  }[] = [];
  for (const k of KATEGORIEN) {
    const eur = cent(Number(roh[k] ?? 0));
    if (!eur) continue;
    zeilen.push({ bezeichnung: KATEGORIE_TEXT[k], eur, posten: POSTEN_JE_KATEGORIE[k] });
  }
  return zeilen;
}

/* ══ Reisen vergleichen ══════════════════════════════════════════════════
 *
 * Die eine Zeile unter einer abgeschlossenen Reise in der Übersicht. Sie ist
 * der Grund, aus dem die Kennzahlen überhaupt gerechnet werden: Eine einzelne
 * Reise mit 0,21 €/km sagt nichts — drei Reisen nebeneinander sagen, welche
 * die teure war.
 */

/**
 * `38 €/Tag · 0,21 €/km` — oder `null`, wenn nichts zu sagen ist.
 *
 * Beide Kennzahlen können einzeln fehlen, und dann steht nur die andere da.
 * Fehlen beide, gibt es **keine** Zeile: Ein Strich unter einer Reise sähe aus
 * wie „hat nichts gekostet", und genau das heißt er nicht. Er heißt „ohne
 * Kilometerstände im Tagebuch und ohne Reisetage lässt sich das nicht sagen".
 *
 * Die Nachkommastellen sind nicht symmetrisch, und das ist Absicht — dieselbe
 * Überlegung wie im Kostenreiter: `€/km` auf ganze Euro gerundet steht bei
 * jeder Reise auf `0 €`, `€/Tag` auf zwei Stellen genau ist eine Genauigkeit,
 * die die Zahl nicht hat (sie hängt an der Zahl der Reisetage).
 *
 * Ohne Anzeigedichte: Die Reiseliste ist die Fläche vor dem Anmelden an eine
 * Reise, und der Experten-Schalter gehört in den Planer. Wer es genauer will,
 * öffnet den Reiter Kosten.
 */
export function vergleichszeile(k: Kennzahlen | null | undefined): string | null {
  const teile: string[] = [];
  if (k?.eur_pro_tag != null && Number.isFinite(k.eur_pro_tag)) {
    teile.push(euroJe(k.eur_pro_tag, 'Tag', 0));
  }
  if (k?.eur_pro_km != null && Number.isFinite(k.eur_pro_km)) {
    teile.push(euroJe(k.eur_pro_km, 'km', 2));
  }
  return teile.length ? teile.join(' · ') : null;
}

/** Das Wenige, was die Auswahl braucht — nicht der ganze `Trip`. */
export interface Vergleichbar extends MitAbschluss {
  id: number;
}

/**
 * Welche Reisen noch einen Kostenabruf brauchen — in der Reihenfolge der Liste.
 *
 * Zwei Bedingungen, und beide sind wichtig:
 *
 * 1. **Nur abgeschlossene.** Eine laufende Reise hat halbe Zahlen: drei von
 *    vierzehn Tagen erfasst, der Rückweg fehlt. `42 €/Tag` an einer Reise, die
 *    noch läuft, ist keine Auskunft, sondern eine Momentaufnahme, die sich
 *    morgen ändert — und daneben stünde sie neben den endgültigen Zahlen der
 *    anderen, ohne sich von ihnen zu unterscheiden.
 * 2. **Nur einmal.** `bekannt` enthält jede Reise, die schon abgerufen wurde,
 *    auch die ohne Ergebnis. Ein zweiter Versuch nach einem Fehlschlag brächte
 *    dieselbe Antwort und kostete dieselbe Rundreise.
 *
 * Die Reihenfolge ist die der Liste und nicht die der Kennzahlen: Abgerufen
 * wird von oben nach unten, also dort zuerst, wo der Blick ohnehin hinfällt.
 */
export function vergleichskandidaten(
  reisen: readonly Vergleichbar[],
  bekannt: ReadonlySet<number>
): number[] {
  return reisen.filter((r) => istAbgeschlossen(r) && !bekannt.has(r.id)).map((r) => r.id);
}
